import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { NavigationChangeRequestsSideSheet } from '@gitbook/app-navigation';
import {
    createPalette,
    PaletteCommandType,
    SidePanelSheet,
    usePaletteHandle,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    Heading,
    List,
    type ListRenderItemInfo,
    ListSkeleton,
    Pagination,
} from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import {
    getChangeRequestStatusDescription,
    useSpaceChangeRequestCommandsFactory,
} from '../../../Components';
import { useSetAnalyticsScreen, useSideSheetNavigation } from '../../../navigation';
import { apiResource, useLoggedInUser, useSuspensePaginatedAPIQuery } from '../../../state';
import { ChangeRequestItem } from './ChangeRequestItem';
import { DotIndicator } from './DotIndicator';

/**
 * Sidepanel to display the change requests of a space.
 */
export function ChangeRequestsListSidePanel(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}): React.ReactElement | null {
    const { space, analyticsContext } = props;
    const { opened, close, setSelectedTab, selectedTab } =
        useSideSheetNavigation<NavigationChangeRequestsSideSheet>(
            'change-requests',
            space.changeRequestsOpen > 0 ? 'open' : 'draft'
        );
    const [filterOption, setFilterOption] = React.useState<ChangeRequestsFilterOptions>(
        ChangeRequestsFilterOptions.All
    );

    useSetAnalyticsScreen(
        opened
            ? { ...analyticsContext, screen: 'space_change_requests', screenTab: selectedTab }
            : null,
        [selectedTab]
    );
    const tabs = React.useMemo(() => {
        return [
            {
                key: api.ChangeRequestStatus.Draft,
                label: getChangeRequestStatusDescription(api.ChangeRequestStatus.Draft),
                trailingIcon:
                    space.changeRequestsDraft > 0 &&
                    filterOption === ChangeRequestsFilterOptions.All
                        ? DotIndicator
                        : undefined,
            },
            {
                key: api.ChangeRequestStatus.Open,
                label: getChangeRequestStatusDescription(api.ChangeRequestStatus.Open),
                trailingIcon:
                    space.changeRequestsOpen > 0 && filterOption === ChangeRequestsFilterOptions.All
                        ? DotIndicator
                        : undefined,
            },
            {
                key: api.ChangeRequestStatus.Merged,
                label: getChangeRequestStatusDescription(api.ChangeRequestStatus.Merged),
            },
            {
                key: api.ChangeRequestStatus.Archived,
                label: getChangeRequestStatusDescription(api.ChangeRequestStatus.Archived),
            },
        ];
    }, [space.changeRequestsOpen, space.changeRequestsDraft, filterOption]);

    return opened ? (
        <SidePanelSheet
            title="Change requests"
            onClose={close}
            selectedTab={selectedTab}
            tabs={tabs}
            onSelectedTabChange={setSelectedTab}
            filters={
                <ChangeRequestsFilterButton
                    key="filter"
                    selectedFilterOption={filterOption}
                    onSelectFilterOption={setFilterOption}
                />
            }
        >
            <React.Suspense fallback={<ListSkeleton className="p-4" />}>
                <ChangeRequestsList
                    space={space}
                    selectedStatus={selectedTab as api.ChangeRequestStatus}
                    selectedFilterOption={filterOption}
                    analyticsContext={analyticsContext}
                />
            </React.Suspense>
        </SidePanelSheet>
    ) : null;
}

enum ChangeRequestsFilterOptions {
    All = 'all',
    CreatedByUser = 'user-created',
    ContributedByUser = 'user-contributed',
}

/**
 * Side panel header button to select the change requests list filters
 */
function ChangeRequestsFilterButton(props: {
    selectedFilterOption: ChangeRequestsFilterOptions;
    onSelectFilterOption: (newFilter: ChangeRequestsFilterOptions) => void;
}) {
    const { selectedFilterOption, onSelectFilterOption } = props;
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const palette = usePaletteHandle(buttonRef);

    const onPress = () => {
        palette.open(
            createPalette({
                groups: [
                    {
                        key: 'filter',
                        title: 'Filter by',
                        commands: [
                            {
                                key: 'all',
                                title: 'All requests',
                                icon: Icon.ChangeRequest,
                                trigger: () => {
                                    onSelectFilterOption(ChangeRequestsFilterOptions.All);
                                },
                                type: PaletteCommandType.RADIO,
                                selected: selectedFilterOption === ChangeRequestsFilterOptions.All,
                            },
                            {
                                key: 'user-created',
                                title: 'My requests',
                                icon: Icon.User,
                                trigger: () => {
                                    onSelectFilterOption(ChangeRequestsFilterOptions.CreatedByUser);
                                },
                                type: PaletteCommandType.RADIO,
                                selected:
                                    selectedFilterOption ===
                                    ChangeRequestsFilterOptions.CreatedByUser,
                            },
                            {
                                key: 'user-contributed',
                                title: 'Participating in',
                                icon: Icon.Users,
                                trigger: () => {
                                    onSelectFilterOption(
                                        ChangeRequestsFilterOptions.ContributedByUser
                                    );
                                },
                                type: PaletteCommandType.RADIO,
                                selected:
                                    selectedFilterOption ===
                                    ChangeRequestsFilterOptions.ContributedByUser,
                            },
                        ],
                    },
                ],
            })
        );
    };

    return (
        <Button
            ref={buttonRef}
            kind="blank"
            leadingIcon={Icon.Filter}
            label="Sort requests"
            active={[
                ChangeRequestsFilterOptions.CreatedByUser,
                ChangeRequestsFilterOptions.ContributedByUser,
            ].includes(selectedFilterOption)}
            onPress={onPress}
            iconOnly
        />
    );
}

/**
 * Content of the change request sidepanel showing all change request with the selected tab status.
 */
function ChangeRequestsList(props: {
    space: api.Space;
    selectedFilterOption: ChangeRequestsFilterOptions;
    selectedStatus: api.ChangeRequestStatus;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { selectedStatus, selectedFilterOption, space, analyticsContext } = props;

    const spaceId = space.id;
    const user = useLoggedInUser();
    const {
        data: { items: changeRequests },
        previous,
        next,
    } = useSuspensePaginatedAPIQuery(
        apiResource('listChangeRequestsForSpace', {
            spaceId,
            status: selectedStatus,
            creator:
                selectedFilterOption === ChangeRequestsFilterOptions.CreatedByUser
                    ? user.uid
                    : undefined,
            contributor:
                selectedFilterOption === ChangeRequestsFilterOptions.ContributedByUser
                    ? user.uid
                    : undefined,
            limit: 20,
        })
    );

    const getChangeRequestCommands = useSpaceChangeRequestCommandsFactory({
        space,
        analyticsContext,
    });
    const anchorRef = React.useRef(null);
    const handle = usePaletteHandle(anchorRef);

    const renderItem = React.useCallback(
        ({ item }: ListRenderItemInfo<api.ChangeRequest>) => {
            return (
                <ChangeRequestItem
                    spaceKey={spaceId}
                    changeRequest={item}
                    onPress={() => {
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'change_request_sidepanel_open',
                            tab: selectedStatus,
                            changeRequest: item.id,
                        });
                    }}
                />
            );
        },
        [spaceId, selectedStatus]
    );

    if (changeRequests.length === 0) {
        return (
            <div className="flex flex-1 p-4">
                <EmptyState>
                    <EmptyStateIcon>
                        <Icon.ChangeRequest />
                    </EmptyStateIcon>
                    <Heading>
                        {selectedStatus === 'archived'
                            ? 'No archived change requests'
                            : `No ${getChangeRequestStatusDescription(
                                  selectedStatus
                              ).toLowerCase()} change requests yet`}
                    </Heading>
                    <Description>
                        {selectedStatus === api.ChangeRequestStatus.Archived
                            ? 'No change requests were archived yet.'
                            : 'Change requests help you collaborate asynchronously on your content with other people.'}
                    </Description>
                    {space.permissions.edit && (
                        <EmptyStateActions>
                            <Button
                                kind="primary"
                                size="medium"
                                leadingIcon={Icon.ChangeRequest}
                                label="New change request"
                                ref={anchorRef}
                                onPress={async () => {
                                    const commands = getChangeRequestCommands();
                                    assert(
                                        commands['space.edit'],
                                        'space.edit command should be defined'
                                    );
                                    await handle.executeCommand(commands['space.edit']);
                                }}
                            />
                        </EmptyStateActions>
                    )}
                </EmptyState>
            </div>
        );
    }
    return (
        <>
            <List items={changeRequests} renderItem={renderItem} className="p-4" />
            <Pagination next={next} previous={previous} />
        </>
    );
}
